<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_VAT_AND_WHT_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="depositBalanceReportTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(amount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(financialDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="depositBalanceRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="number"
                for="account-number"
              />
            </b-col>
            <!-- Account Number -->

            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product Name')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->
            <!-- Financial Date From -->
            <b-col md="6">
              <DatePickerField
                id="financial-date From"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Financial Date From')"
              />
            </b-col>
            <!-- Financial Date From -->
            <!-- Financial Date To -->
            <b-col md="6">
              <DatePickerField
                id="financial-date To"
                v-model="filter.financialDateTo"
                :min-date="new Date()"
                :label="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Financial Date To')"
              />
            </b-col>
            <!-- Financial Date To -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <!-- Customer Type -->
            <b-col md="6">
              <SelectField
                id="customer-type"
                v-model="filter.customerType"
                :reduce="option => option.value"
                :label="$t('Customer Type')"
                :placeholder="$t('Select')"
                :options="translatedCustomerTypeOptions"
                item="label"
              />
            </b-col>
            <!-- Customer Type -->
            <!-- Tax Type -->
            <b-col md="6">
              <SelectField
                id="customer-type"
                v-model="filter.taxType"
                :reduce="option => option.value"
                :label="$t('Tax Type')"
                :placeholder="$t('Select')"
                :options="translatedTaxTypeOptions"
                item="label"
              />
            </b-col>
            <!-- Tax Type -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
// import jsPDF from 'jspdf'
// import 'jspdf-autotable'
import XLSX from 'xlsx'
import { heightFade } from '@core/directives/animations'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import toNumber from '../../../../helpers/toNumber'
import { toCustomDate } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isDisabled: false,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Customer Account Balance Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'product', sortable: true, label: 'Product Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'taxType', sortable: true, label: 'Tax Type' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'referenceNumber', sortable: true, label: 'Posting Reference' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'customerType', sortable: true, label: 'Customer Type' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
      ],

      customerTypeOptions: [
        { label: 'Corporate', value: 'Corporate' },
        { label: 'Group - Joint', value: 'GroupJoint' },
        { label: 'Individual', value: 'Individual' },
      ],
      taxTypeOptions: [
        { label: 'VAT', value: 'VAT' },
        { label: 'WHT', value: 'WHT' },
      ],
      filter: {
        accountNumber: '',
        financialDateFrom: null,
        financialDateTo: null,
        productCode: '',
        branchCode: '',
        customerType: '',
        taxType: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      productOptions: [],
      accountOfficerOptions: [],
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Account Name': item.accountName,
        'Account Number': item.accountNumber,
        Product: item.product,
        Branch: item.branch,
        'Tax Type': item.taxType,
        Amount: this.toAmount(item.amount),
        'Posting Reference': item.referenceNumber,
        Narration: item.narration,
        'Customer Type': item.customerType,
        'Financial Date': item.financialDate ? this.formatCustomDate(item.financialDate) : '',
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedCustomerTypeOptions() {
      return this.customerTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedTaxTypeOptions() {
      return this.taxTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchDepositBalanceReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchDepositBalanceReport()
    },
  },
  mounted() {
    this.fetchDepositBalanceReport()
    this.fetchProduct()
    this.fetchAccountOfficer()
  },
  methods: {
    ...mapActions({
      getProduct: 'account/getProducts',
      getBranchByCode: 'operations/getBranchByCode',
      getVatWHTReports: 'reports/getVatWHTReports',
      getAccountOfficer: 'account/getAccountOfficerList',
    }),
    resetFilters() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
      this.fetchDepositBalanceReport()
    },
    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabled = false
      } else {
        this.isDisabled = true
        this.filter.financialDate = null
      }
    },

    async fetchAccountOfficer() {
      try {
        const response = await this.getAccountOfficer({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.VAT_AND_WHT_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.VAT_AND_WHT_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      if (this.filter.useCurrentFinancialDate === false) {
        bvModalEvt.preventDefault()
        this.$refs.depositBalanceRules.validate().then(async success => {
          if (success) {
            this.fetchDepositBalanceReport()
          }
        })
      } else {
        this.fetchDepositBalanceReport()
      }
    },
    async fetchDepositBalanceReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getVatWHTReports({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getVatWHTReports({
          search: this.filter,
          page: 1,
          limit: 10000000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['VAT & WHT Report'],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'VAT-WHT-Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getVatWHTReports({
          search: this.filter,
          page: 1,
          limit: 10000000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'VAT-WHT-Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['VAT & WHT Report'],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = arrData // All data lists
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'VAT & WHT Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatAmount: toBalance,
    toAmount: formatAmount,
    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
  },
}
</script>

  <style lang="scss" scoped>
  .per-page-selector {
    width: 90px;
  }

  .invoice-filter-select {
    min-width: 190px;

    ::v-deep .vs__selected-options {
      flex-wrap: nowrap;
    }

    ::v-deep .vs__selected {
      width: 100px;
    }
  }
  </style>

  <style lang="scss">
      @import '@core/scss/vue/libs/vue-select.scss';
  </style>
